import { calculateEventPosition, generateHourMarkers } from "@/lib/calendarPositioning";
import { EventBlock } from "./EventBlock";
import { AvailabilityShading } from "./AvailabilityShading";
import { ConflictBars } from "./ConflictBars";
import { BusyBars } from "./BusyBars";
import { JobStatus } from "../StatusBadge";
import { type AssignedStaffMember } from "@/lib/calendarSpans";

interface CalendarEvent {
  id: string;
  jobNumber?: string;
  title: string;
  date: Date;
  scheduledStartDate?: Date;
  scheduledEndDate?: Date;
  status: JobStatus;
  customer: string;
  address?: string;
  phone?: string;
  assignedTo?: string;
  assignedStaff?: AssignedStaffMember[];
  staffColor?: string;
  conflictColor?: string;
  statusColor?: string;
  jobTypeColor?: string;
  providerColor?: string;
  source?: string;
  highestConflictSeverity?: string;
  onSelect?: () => void;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  workdayStart: string;
  workdayEnd: string;
}

interface WeekViewDayColumnProps {
  day: Date;
  events: CalendarEvent[];
  staff: StaffMember;
  onEventMove?: (update: {
    eventId: string;
    newStart: Date;
    newEnd: Date;
    staffId?: string;
  }) => void;
  onOpenJob?: (jobId: string) => void;
  onCreateJob?: (startTime: Date, staffId: string) => void;
}

export function WeekViewDayColumn({ day, events, staff, onEventMove, onOpenJob, onCreateJob }: WeekViewDayColumnProps) {
  const hourMarkers = generateHourMarkers(0, 24);
  const dayHeight = 24 * 60; // 60px per hour, 24 hours
  
  // Filter busy blocks (imported calendar events from Google/Outlook)
  const busyBlocks = events.filter(ev => ev.source && ev.source !== "workdeck");

  // Handle double-click to create new job (Outlook-like)
  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onCreateJob) return;
    
    // Calculate the time based on click position
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    
    // Convert pixel position to time (60px per hour = 1px per minute)
    const minutes = Math.round(y);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    const startTime = new Date(day);
    startTime.setHours(hours, mins, 0, 0);
    
    onCreateJob(startTime, staff.id);
  };

  return (
    <div
      className="border-l relative cursor-cell hover:bg-blue-50/30 transition-colors duration-150"
      style={{ 
        height: `${dayHeight}px`,
        borderColor: 'var(--wd-gray-200)',
        backgroundColor: 'var(--wd-gray-50)',
        paddingLeft: '2px',
        paddingRight: '2px',
      }}
      onDoubleClick={handleDoubleClick}
    >
      {/* Hour lines - Outlook-style soft grid */}
      {hourMarkers.map((marker) => (
        <div
          key={marker.hour}
          className="absolute left-0 right-0 border-t"
          style={{ 
            top: `${marker.position}px`,
            borderColor: 'var(--wd-gray-200)'
          }}
        />
      ))}

      {/* Busy Bars Layer - Outlook-style busy shading for personal calendar events */}
      <BusyBars 
        events={events.map(event => ({
          id: event.id,
          startTime: event.scheduledStartDate ? new Date(event.scheduledStartDate) : new Date(event.date),
          endTime: event.scheduledEndDate ? new Date(event.scheduledEndDate) : new Date(event.date),
          source: event.source,
        }))}
        dayStart={0}
      />

      {/* Conflict Bars Layer - Red/yellow diagonal stripes for scheduling conflicts */}
      <ConflictBars 
        events={events.map(event => ({
          id: event.id,
          startTime: event.scheduledStartDate ? new Date(event.scheduledStartDate) : new Date(event.date),
          endTime: event.scheduledEndDate ? new Date(event.scheduledEndDate) : new Date(event.date),
          highestConflictSeverity: event.highestConflictSeverity,
        }))}
        dayStart={0}
      />

      {/* Availability Shading - Work hours and busy times (comprehensive 3-layer system) */}
      <AvailabilityShading
        busyBlocks={busyBlocks.map(block => ({
          id: block.id,
          startTime: block.scheduledStartDate ? new Date(block.scheduledStartDate) : new Date(block.date),
          endTime: block.scheduledEndDate ? new Date(block.scheduledEndDate) : new Date(block.date),
          source: block.source,
        }))}
        conflictEvents={events.map(event => ({
          id: event.id,
          startTime: event.scheduledStartDate ? new Date(event.scheduledStartDate) : new Date(event.date),
          endTime: event.scheduledEndDate ? new Date(event.scheduledEndDate) : new Date(event.date),
          highestConflictSeverity: event.highestConflictSeverity,
        }))}
        staffWorkStart={staff.workdayStart}
        staffWorkEnd={staff.workdayEnd}
        dayStart={0}
        dayEnd={24}
      />

      {/* Events */}
      {events.map((event) => {
        // Use scheduledStartDate/scheduledEndDate if available, fallback to date
        // Normalize to Date objects in case API returns ISO strings
        const eventStart = event.scheduledStartDate 
          ? new Date(event.scheduledStartDate) 
          : new Date(event.date);
        const eventEnd = event.scheduledEndDate 
          ? new Date(event.scheduledEndDate) 
          : new Date(event.date);
        
        // Clamp start/end times to the current day's boundaries
        // This ensures multi-day events render correctly in each day column
        const dayStart = new Date(day);
        dayStart.setHours(0, 0, 0, 0);
        
        const dayEnd = new Date(day);
        dayEnd.setHours(23, 59, 59, 999);
        
        // For multi-day events:
        // - Start at event start time (if event starts this day) or midnight (if event started earlier)
        // - End at event end time (if event ends this day) or 23:59 (if event continues later)
        const clampedStart = eventStart > dayStart ? eventStart : dayStart;
        const clampedEnd = eventEnd < dayEnd ? eventEnd : dayEnd;
        
        const pos = calculateEventPosition(clampedStart, clampedEnd);
        
        return (
          <EventBlock
            key={event.id}
            id={event.id}
            title={event.title}
            jobNumber={event.jobNumber}
            customer={event.customer}
            address={event.address}
            phone={event.phone}
            date={clampedStart}
            endDate={clampedEnd}
            status={event.status}
            assignedStaff={event.assignedStaff}
            top={pos.top}
            height={pos.height}
            conflictColor={event.conflictColor}
            statusColor={event.statusColor}
            jobTypeColor={event.jobTypeColor}
            providerColor={event.providerColor}
            source={event.source}
            highestConflictSeverity={event.highestConflictSeverity}
            staffId={staff.id}
            onClick={event.onSelect}
            onMove={onEventMove}
            onOpenJob={onOpenJob}
          />
        );
      })}
    </div>
  );
}
